import {
    Badge,
    Button,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityRO } from '@routellers/types/dist/city/city.ro';
import { GygActivityStatusEnum } from '@routellers/types/dist/gyg-activity/enum/gyg-activity-status.enum';
import {
    GygActivityRO,
    GygTourSearchHitRO,
} from '@routellers/types/dist/gyg-activity/gyg-activity.ro';
import { IconExternalLink, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { slicePage } from '../utils/list';

const formatPrice = (row: Pick<GygActivityRO, 'priceFrom' | 'currency'>) => {
    if (row.priceFrom == null) return '—';

    return `${row.currency || ''} ${row.priceFrom}`.trim();
};

export const ActivitiesPage = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const cityIdParam = searchParams.get('cityId');
    const cityId = cityIdParam ? Number(cityIdParam) : null;

    const [rows, setRows] = useState<GygActivityRO[]>([]);
    const [cities, setCities] = useState<CityRO[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [configured, setConfigured] = useState<boolean | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [searchQ, setSearchQ] = useState('');
    const [hits, setHits] = useState<GygTourSearchHitRO[]>([]);
    const [searching, setSearching] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);

    const cityOptions = useMemo(
        () =>
            cities.map((city) => ({
                value: String(city.id),
                label: city.title,
            })),
        [cities],
    );

    const loadCities = useCallback(async () => {
        try {
            const { data } = await api.cities.query({
                page: 1,
                limit: 500,
                sort: 'title',
                order: 'ASC',
                where: { readyForRoutell: null } as unknown as Record<string, never>,
            });

            setCities(data?.items || []);
        } catch {
            setCities([]);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const [{ data: config }, { data }] = await Promise.all([
                api.gygActivitiesApi.config(),
                api.gygActivitiesApi.list(cityId ? { cityId } : undefined),
            ]);

            setConfigured(Boolean(config?.configured));
            setRows(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [cityId]);

    useEffect(() => {
        void loadCities();
    }, [loadCities]);

    useEffect(() => {
        void load();
    }, [load]);

    const runSearch = async () => {
        if (!cityId) {
            notifications.show({ color: 'yellow', message: t('pages.activities.pickCityFirst') });

            return;
        }

        setSearching(true);

        try {
            const { data } = await api.gygActivitiesApi.search({
                cityId,
                q: searchQ.trim() || undefined,
                limit: 20,
            });

            setHits(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setHits([]);
        } finally {
            setSearching(false);
        }
    };

    const attach = async (hit: GygTourSearchHitRO) => {
        if (!cityId || hit.alreadyAttached) return;

        setBusyId(hit.gygTourId);

        try {
            await api.gygActivitiesApi.create({
                data: { cityId, gygTourId: hit.gygTourId },
            });
            notifications.show({ color: 'green', message: t('pages.activities.attached') });
            setHits((prev) =>
                prev.map((row) =>
                    row.gygTourId === hit.gygTourId ? { ...row, alreadyAttached: true } : row,
                ),
            );
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    const setStatus = async (row: GygActivityRO, status: GygActivityStatusEnum) => {
        setBusyId(row.id);

        try {
            await api.gygActivitiesApi.update({ id: row.id, data: { status } });
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    const syncRow = async (row: GygActivityRO) => {
        setBusyId(row.id);

        try {
            await api.gygActivitiesApi.sync(row.id);
            notifications.show({ color: 'green', message: t('common.updated') });
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    const removeRow = async (row: GygActivityRO) => {
        if (!window.confirm(t('common.confirmDelete'))) return;

        setBusyId(row.id);

        try {
            await api.gygActivitiesApi.remove(row.id);
            notifications.show({ color: 'green', message: t('common.deleted') });
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    const pageRows = slicePage(rows, page);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.activities.title')}
                action={
                    <Group>
                        <Select
                            clearable
                            searchable
                            placeholder={t('pages.activities.filterCity')}
                            data={cityOptions}
                            value={cityId ? String(cityId) : null}
                            onChange={(value) => {
                                setPage(1);

                                if (value) {
                                    setSearchParams({ cityId: value });
                                } else {
                                    setSearchParams({});
                                }
                            }}
                            w={260}
                        />
                        <Button
                            leftSection={<IconPlus size={16} />}
                            disabled={!cityId}
                            onClick={() => {
                                setAddOpen(true);
                                setHits([]);
                                setSearchQ('');
                            }}
                        >
                            {t('pages.activities.add')}
                        </Button>
                    </Group>
                }
            />

            {configured === false ? (
                <Text c='orange' size='sm'>
                    {t('pages.activities.notConfigured')}
                </Text>
            ) : null}

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={900}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.activities.columns.tour')}</Table.Th>
                                    <Table.Th>{t('common.city')}</Table.Th>
                                    <Table.Th>{t('pages.activities.columns.price')}</Table.Th>
                                    <Table.Th>{t('common.status')}</Table.Th>
                                    <Table.Th>{t('pages.activities.columns.featured')}</Table.Th>
                                    <Table.Th>{t('common.actions')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => (
                                    <Table.Tr key={row.id}>
                                        <Table.Td>
                                            <EntityThumb
                                                src={row.coverUrl}
                                                name={row.title}
                                                subtitle={`GYG #${row.gygTourId}`}
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            {row.cityId ? (
                                                <Text
                                                    component={Link}
                                                    to={`/cities/${row.cityId}`}
                                                    size='sm'
                                                    c='blue'
                                                >
                                                    {row.cityTitle || `#${row.cityId}`}
                                                </Text>
                                            ) : (
                                                '—'
                                            )}
                                        </Table.Td>
                                        <Table.Td>{formatPrice(row)}</Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={
                                                    row.status === GygActivityStatusEnum.PUBLISHED
                                                        ? 'green'
                                                        : 'gray'
                                                }
                                                variant='light'
                                            >
                                                {t(`pages.activities.status.${row.status}`)}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            {row.featured ? (
                                                <Badge color='yellow' variant='light'>
                                                    {t('pages.activities.featured')}
                                                </Badge>
                                            ) : (
                                                '—'
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Group gap='xs' wrap='nowrap'>
                                                <Switch
                                                    size='sm'
                                                    checked={
                                                        row.status ===
                                                        GygActivityStatusEnum.PUBLISHED
                                                    }
                                                    disabled={busyId === row.id}
                                                    onChange={(event) =>
                                                        void setStatus(
                                                            row,
                                                            event.currentTarget.checked
                                                                ? GygActivityStatusEnum.PUBLISHED
                                                                : GygActivityStatusEnum.DRAFT,
                                                        )
                                                    }
                                                    label={t('pages.activities.publish')}
                                                />
                                                <Button
                                                    size='compact-xs'
                                                    variant='light'
                                                    leftSection={<IconRefresh size={14} />}
                                                    loading={busyId === row.id}
                                                    onClick={() => void syncRow(row)}
                                                >
                                                    {t('common.sync')}
                                                </Button>
                                                {row.bookUrl ? (
                                                    <Button
                                                        size='compact-xs'
                                                        variant='subtle'
                                                        component='a'
                                                        href={row.bookUrl}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                        leftSection={<IconExternalLink size={14} />}
                                                    >
                                                        GYG
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    size='compact-xs'
                                                    color='red'
                                                    variant='light'
                                                    leftSection={<IconTrash size={14} />}
                                                    loading={busyId === row.id}
                                                    onClick={() => void removeRow(row)}
                                                >
                                                    {t('common.delete')}
                                                </Button>
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!pageRows.length ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={6}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.activities.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : null}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={rows.length} onChange={setPage} />
                </>
            )}

            <Modal
                opened={addOpen}
                onClose={() => setAddOpen(false)}
                title={t('pages.activities.addTitle')}
                size='xl'
            >
                <Stack>
                    <Group align='flex-end'>
                        <TextInput
                            style={{ flex: 1 }}
                            label={t('common.search')}
                            placeholder={t('pages.activities.searchPlaceholder')}
                            value={searchQ}
                            onChange={(event) => setSearchQ(event.currentTarget.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') void runSearch();
                            }}
                        />
                        <Button loading={searching} onClick={() => void runSearch()}>
                            {t('common.search')}
                        </Button>
                    </Group>

                    {searching ? <Loader size='sm' /> : null}

                    <Stack gap='sm'>
                        {hits.map((hit) => (
                            <Group
                                key={hit.gygTourId}
                                justify='space-between'
                                p='sm'
                                style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}
                            >
                                <EntityThumb
                                    src={hit.coverUrl}
                                    name={hit.title}
                                    subtitle={
                                        hit.teaser
                                            ? hit.teaser.slice(0, 120)
                                            : `GYG #${hit.gygTourId}`
                                    }
                                />
                                <Button
                                    disabled={hit.alreadyAttached}
                                    loading={busyId === hit.gygTourId}
                                    onClick={() => void attach(hit)}
                                >
                                    {hit.alreadyAttached
                                        ? t('pages.activities.alreadyAttached')
                                        : t('pages.activities.attach')}
                                </Button>
                            </Group>
                        ))}
                        {!searching && hits.length === 0 ? (
                            <Text c='dimmed' size='sm'>
                                {t('pages.activities.searchHint')}
                            </Text>
                        ) : null}
                    </Stack>
                </Stack>
            </Modal>
        </Stack>
    );
};
